"use client";

import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import { type ReactNode } from "react";

type RootProps = {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string | number | null) => void;
  children: ReactNode;
  className?: string;
};

function TabsRoot({
  defaultValue,
  value,
  onValueChange,
  children,
  className,
}: RootProps) {
  return (
    <BaseTabs.Root
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      className={className}
    >
      {children}
    </BaseTabs.Root>
  );
}

function TabsList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <BaseTabs.List
      className={`relative flex items-center gap-4 border-b ${className ?? ""}`}
    >
      {children}
      <TabsIndicator />
    </BaseTabs.List>
  );
}

function TabsTab({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <BaseTabs.Tab
      value={value}
      className={`hoverable cursor-default select-none rounded-t-lg p-2 text-lg text-muted outline-none data-active:font-semibold data-active:text-primary ${className ?? ""}`}
    >
      {children}
    </BaseTabs.Tab>
  );
}

function TabsPanel({
  value,
  children,
  className,
  keepMounted,
}: {
  value: string;
  children: ReactNode;
  className?: string;
  keepMounted?: boolean;
}) {
  return (
    <BaseTabs.Panel
      value={value}
      keepMounted={keepMounted}
      className={`data-hidden:hidden ${className ?? ""}`}
    >
      {children}
    </BaseTabs.Panel>
  );
}

function TabsIndicator() {
  return (
    <BaseTabs.Indicator className="absolute bottom-0 left-0 h-0.5 w-(--active-tab-width) translate-x-(--active-tab-left) bg-primary transition-all duration-200 ease-in-out" />
  );
}

export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel,
};
